/**
 * SkeletonHome - Skeleton loading placeholder for the home screen.
 *
 * Displays a shimmer-animated wireframe of the full home layout:
 *   - Widget banner row (4 cards)
 *   - App suggestions row (6 icons)
 *   - App grid (12 tiles = 3 rows x 4 cols)
 *   - Dock (3 shortcut icons)
 *
 * Shown while all async steps (auth, catalog, weather, etc.) complete,
 * then fades out as the real home screen fades in.
 */
import { useState, useCallback } from 'react';
import { HOME_MEDIA } from '@room/shared-ui';
import './SkeletonHome.css';

function SkeletonWidgetCards() {
  return (
    <div className="skeleton-banner">
      <div className="skeleton-banner__scroll">
        <div className="skeleton-widget skeleton-widget--datetime">
          <div className="skeleton-pulse skeleton-line" style={{ width: '60%', height: 24 }} />
          <div className="skeleton-pulse skeleton-line" style={{ width: '80%', height: 10, marginTop: 6 }} />
          <div className="skeleton-pulse skeleton-line" style={{ width: '55%', height: 9, marginTop: 4 }} />
        </div>
        <div className="skeleton-widget skeleton-widget--weather">
          <div className="skeleton-pulse skeleton-circle" style={{ width: 30, height: 30 }} />
          <div className="skeleton-pulse skeleton-line" style={{ width: '50%', height: 18, marginTop: 4 }} />
          <div className="skeleton-pulse skeleton-line" style={{ width: '65%', height: 8, marginTop: 3 }} />
        </div>
        <div className="skeleton-widget skeleton-widget--notif">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="skeleton-pulse skeleton-circle" style={{ width: 16, height: 16 }} />
            <div className="skeleton-pulse skeleton-line" style={{ width: 60, height: 10 }} />
          </div>
          <div className="skeleton-pulse skeleton-line" style={{ width: '80%', height: 9, marginTop: 10 }} />
        </div>
        <div className="skeleton-widget skeleton-widget--banner">
          <div className="skeleton-pulse skeleton-circle" style={{ width: 28, height: 28, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton-pulse skeleton-line" style={{ width: '70%', height: 10 }} />
            <div className="skeleton-pulse skeleton-line" style={{ width: '55%', height: 8, marginTop: 4 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonSuggestions() {
  return (
    <div className="skeleton-suggestions">
      <div className="skeleton-suggestions__scroll">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton-suggestions__item">
            <div className="skeleton-pulse skeleton-icon skeleton-icon--suggestion" />
            <div className="skeleton-pulse skeleton-line" style={{ width: 38, height: 8, marginTop: 4 }} />
            <div className="skeleton-pulse skeleton-pill" />
          </div>
        ))}
        {/* "See All" placeholder */}
        <div className="skeleton-suggestions__item">
          <div className="skeleton-pulse skeleton-icon skeleton-icon--suggestion" />
          <div className="skeleton-pulse skeleton-line" style={{ width: 38, height: 8, marginTop: 4 }} />
        </div>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="skeleton-grid">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="skeleton-tile">
          <div className="skeleton-pulse skeleton-icon" />
          <div className="skeleton-pulse skeleton-line" style={{ width: 40, height: 8, marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonDock() {
  return (
    <div className="skeleton-dock">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="skeleton-tile skeleton-tile--dock">
          <div className="skeleton-pulse skeleton-icon skeleton-icon--dock" />
          <div className="skeleton-pulse skeleton-line" style={{ width: 32, height: 7, marginTop: 4 }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonWallpaper() {
  const entry = HOME_MEDIA['home-wallpaper'];
  const [loaded, setLoaded] = useState(false);
  const handleLoad = useCallback(() => setLoaded(true), []);

  if (!entry) return null;

  return (
    <div className={`skeleton-wallpaper ${loaded ? 'skeleton-wallpaper--loaded' : ''}`}>
      <img
        className="skeleton-wallpaper__img"
        src={`/api/media/${entry.id}.png`}
        alt=""
        draggable={false}
        onLoad={handleLoad}
        onError={(e) => (e.currentTarget.style.display = 'none')}
      />
      <div className="skeleton-wallpaper__overlay" />
    </div>
  );
}

export function SkeletonHome() {
  return (
    <div className="skeleton-home" aria-hidden="true">
      <SkeletonWallpaper />
      <SkeletonWidgetCards />
      <SkeletonSuggestions />
      <SkeletonGrid />
      <SkeletonDock />
    </div>
  );
}
